<template>
	<PageLoading :loading="battles.loading" class="relative mt-4 pb-20">
		<PageTitle>{{ t('title') }}</PageTitle>
		<PagePillRow class="mt-4">
			<BaseButton
				color="secondary"
				size="md"
				@click="toggleSort"
				class="snap-start"
			>
				<SortIcon
					class="h-5 w-5 transition-transform"
					:class="{ 'rotate-180': !sortExpensive }"
				/>
				<div>{{ sortText }}</div>
			</BaseButton>
			<div class="bg-separator-secondary mx-2 h-4 w-px"></div>
			<TagGroup class="*:snap-start" v-model="selectedTag" :tags="filters" />
		</PagePillRow>
		<PagePadding class="mt-4 space-y-1.5">
			<BattleCard
				v-for="item in battlesSorted"
				:key="item.uuid"
				:uuid="item.uuid"
				:status="item.status"
				:participants="item.participants"
				:cases="item.cases"
				:entryFee="item.entryFee"
				:gameMode="item.gameMode"
				:modifiers="item.modifiers"
				:startedAt="item.ts"
				:tieBreakWinner="item.tieBreakWinner"
				:funding="item.funding"
				@setStatus="setStatus(item.uuid, $event)"
			/>
		</PagePadding>
		<PagePadding
			class="space-y-1.5 transition-all duration-500"
			:class="{ 'mt-0': !battlesSorted.length, 'mt-8': battlesSorted.length }"
		>
			<BattleCard
				v-for="item in finishedBattles"
				:key="item.uuid"
				:uuid="item.uuid"
				:status="item.status"
				:participants="item.participants"
				:cases="item.cases"
				:entryFee="item.entryFee"
				:gameMode="item.gameMode"
				:modifiers="item.modifiers"
				:startedAt="item.ts"
				:funding="item.funding"
				:tieBreakWinner="item.tieBreakWinner"
				@setStatus="setStatus(item.uuid, $event)"
			/>
		</PagePadding>
		<BottomActions>
			<RouterLink to="/battles/create" custom v-slot="{ navigate }">
				<BaseButton
					class="w-full"
					size="lg"
					color="primary"
					@click="resetAndNavigate(navigate)"
				>
					<BattlesIcon class="h-5 w-5" />
					<div>{{ t('create') }}</div>
				</BaseButton>
			</RouterLink>
		</BottomActions>
	</PageLoading>
</template>
<script setup lang="ts">
import PageTitle from '@/components/PageTitle.vue'
import PagePadding from '@/components/PagePadding.vue'
import PagePillRow from '@/components/PagePillRow.vue'
import BaseButton from '@/components/BaseButton.vue'
import TagGroup from '@/components/TagGroup.vue'
import SortIcon from '@/assets/icons/sort.svg'
import AllIcon from '@/assets/icons/all.svg'
import JoinableIcon from '@/assets/icons/joinable.svg'
import { ref, computed } from 'vue'
import BattleCard from '@/components/BattleCard.vue'
import { useLayout } from '@/stores/layout'
import BottomActions from '@/components/BottomActions.vue'
import BattlesIcon from '@/assets/icons/battles.svg'
import { useBattles } from '@/stores/battles'
import PageLoading from '@/components/PageLoading.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({
	messages: {
		en: {
			title: 'battles',
			create: 'create battle',
			filters: {
				all: 'All battles',
				joinable: 'Joinable'
			},
			sort: {
				expensive: 'Expensive first',
				cheap: 'Cheap first'
			}
		},
		ru: {
			title: 'батлы',
			create: 'создать батл',
			filters: {
				all: 'Все батлы',
				joinable: 'Можно вступить'
			},
			sort: {
				expensive: 'Сначала дорогие',
				cheap: 'Сначала дешевые'
			}
		}
	}
})

const layout = useLayout()
const battles = useBattles()

layout.showBottomNavbar()

const sortExpensive = ref(true)

const toggleSort = () => {
	sortExpensive.value = !sortExpensive.value
}

const sortText = computed(() => {
	return sortExpensive.value ? t('sort.expensive') : t('sort.cheap')
})

const battlesSorted = computed(() => {
	return battles.list
		.filter(
			(item) =>
				(item.status === 'created' || selectedTag.value === 0) &&
				item.status !== 'finished'
		)
		.sort((a, b) => {
			return sortExpensive.value
				? b.entryFee - a.entryFee
				: a.entryFee - b.entryFee
		})
})

const finishedBattles = computed(() => {
	return battles.list
		.filter((item) => item.status === 'finished' && selectedTag.value !== 1)
		.sort((a, b) => {
			return sortExpensive.value
				? (b.ts || 0) - (a.ts || 0)
				: (a.ts || 0) - (b.ts || 0)
		})
})

const selectedTag = ref(0)

const filters = computed(() => [
	{ text: t('filters.all'), icon: AllIcon },
	{ text: t('filters.joinable'), icon: JoinableIcon }
])

const setStatus = (
	uuid: string,
	status: 'running' | 'finished' | 'created' | 'waiting_for_block'
) => battles.setStatus(uuid, status)

const resetAndNavigate = (navigate: () => void) => {
	battles.reset()
	navigate()
}
</script>
